import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { WhopCheckoutEmbed } from "@whop/checkout/react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

interface Order { order_id: string; plan_id: string; checkout_id: string; amount: number; package_name: string; name: string; email: string; phone: string; status: string; adults: number; minors: number; infants: number; transport?: string; check_in?: string | null; check_out?: string | null; lang: "es" | "en" }

const T = {
  es: { title: "Detalles de la reservación", confirmation: "Confirmación", guestName: "Nombre del huésped", arrival: "Fecha de llegada", departure: "Fecha de salida", nights: "Número de noches", adults: "Adultos", minors: "Menores", infants: "Infantes", transport: "Transportación", resortCredits: "Resort Credits", total: "Total", pay: "Pago seguro", loading: "Cargando…", notfound: "Esta orden no existe.", paid: "Esta orden ya fue pagada. ¡Gracias!", cancelled: "Este link de pago fue cancelado. Contacta a tu asesor.", secure: "Procesado de forma segura por Whop." },
  en: { title: "Reservation details", confirmation: "Confirmation", guestName: "Guest Name", arrival: "Arrival Date", departure: "Departure Date", nights: "Number of Nights", adults: "Adults", minors: "Minors", infants: "Infants", transport: "Transportation", resortCredits: "Resort Credits", total: "Total", pay: "Secure payment", loading: "Loading…", notfound: "This order does not exist.", paid: "This order has already been paid. Thank you!", cancelled: "This payment link was cancelled. Please contact your advisor.", secure: "Securely processed by Whop." },
};

const fmtDate = (iso: string | null | undefined, lang: string) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return lang === "en" ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
};
const fmtUSD = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;

export function BrandHeader() {
  return (
    <header className="flex items-center px-6 py-5" data-testid="pay-brand-header">
      <span className="text-lg sm:text-xl font-extrabold tracking-wide text-black uppercase">Legendary Vacation Club</span>
    </header>
  );
}

export interface SummaryData { order_id?: string; name: string; amount: number; adults: number; minors: number; infants: number; transport?: string; resort_credits?: number | null; check_in?: string | null; check_out?: string | null }

export function ReservationSummary({ o, lang, testId = "pay-summary" }: { o: SummaryData; lang: "es" | "en"; testId?: string }) {
  const t = T[lang];
  const nights = o.check_in && o.check_out ? Math.max(0, Math.round((Date.parse(o.check_out) - Date.parse(o.check_in)) / 86400000)) : null;
  const groups: [string, string | number | null | undefined][][] = [
    [[t.confirmation, o.order_id ? o.order_id.slice(0, 8).toUpperCase() : "XXXXXXXX"]],
    [[t.guestName, o.name], [t.arrival, fmtDate(o.check_in, lang)], [t.departure, fmtDate(o.check_out, lang)]],
    [[t.nights, nights], [t.adults, o.adults], [t.minors, o.minors || null], [t.infants, o.infants || null], [t.transport, o.transport], [t.resortCredits, o.resort_credits !== null && o.resort_credits !== undefined ? fmtUSD(o.resort_credits) : null]],
  ].map((g) => g.filter(([, v]) => v !== null && v !== undefined && v !== "")).filter((g) => g.length) as any;
  return (
    <section className="rounded-md bg-[#F2EEF9] px-6 py-6 sm:px-8" style={{ fontFamily: "Arial, Helvetica, sans-serif" }} data-testid={testId}>
      <img src="/images/hard-rock-hotel.png" alt="Hard Rock Hotel" className="mx-auto mb-5 w-44 sm:w-52" data-testid={`${testId}-logo`} />
      <h1 className="text-base sm:text-lg font-bold uppercase tracking-[0.12em] text-[#5C4F9C]">{t.title}</h1>
      <div className="mt-6 space-y-5 text-[15px] leading-6 text-[#3A3A3A]">
        {groups.map((g, i) => (
          <div key={i}>
            {g.map(([k, v]) => <p key={k} data-testid={`${testId}-row-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><span>{k}:</span> <span className="font-medium">{v}</span></p>)}
          </div>
        ))}
        <p className="pt-2 text-base font-bold text-[#5C4F9C]" data-testid={`${testId}-total`}>{t.total}: {fmtUSD(o.amount)}</p>
      </div>
    </section>
  );
}

export default function PayOrder() {
  const { orderId = "" } = useParams();
  const [o, setO] = useState<Order | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/pay/${orderId}`).then(({ data }) => setO(data)).catch(() => setErr(true)); }, [orderId]);
  const lang: "es" | "en" = o?.lang === "en" ? "en" : "es";
  const t = T[lang];
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800">
      <BrandHeader />
      <main className="mx-auto max-w-6xl px-4 pb-16">
        {err && <p className="mt-10 text-center text-slate-600" data-testid="pay-not-found">{T.es.notfound} / {T.en.notfound}</p>}
        {!o && !err && <div className="mt-20 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>}
        {o && (
          <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr] items-start">
            <ReservationSummary o={o} lang={lang} />
            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm" data-testid="pay-checkout">
              <h2 className="mb-3 text-base md:text-lg font-semibold text-[#0B192C]">{t.pay}</h2>
              {o.status === "paid" && <p className="rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="pay-already-paid">{t.paid}</p>}
              {(o.status === "cancelled" || o.status === "refunded") && <p className="rounded-lg bg-slate-100 p-4 text-sm text-slate-700" data-testid="pay-cancelled">{t.cancelled}</p>}
              {o.status === "checkout_created" && (
                <div data-testid="whop-embed">
                  <WhopCheckoutEmbed planId={o.plan_id} sessionId={o.checkout_id} prefill={{ email: o.email, name: o.name, phone: o.phone, address: { name: o.name } } as any}
                    returnUrl={`${window.location.origin}/compra-exitosa`} theme="light" fallback={<div className="grid h-64 place-items-center text-sm text-slate-500">{t.loading}</div>} />
                </div>
              )}
              <p className="mt-3 text-center text-xs text-slate-400">{t.secure}</p>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
